import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Building2, Calendar, Clock, ArrowLeft } from 'lucide-react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BookingSuccess = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading'); // loading, success, error
    const [booking, setBooking] = useState(null);
    const sessionId = searchParams.get('session_id');
    const isFree = searchParams.get('free') === 'true';

    useEffect(() => {
        if (isFree) {
            // Free booking (internal user) - already confirmed
            setStatus('success');
        } else if (sessionId) {
            checkPaymentStatus();
        } else {
            setStatus('error');
        }
    }, [sessionId, isFree]);

    const checkPaymentStatus = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/booking/checkout/status/${sessionId}`);
            
            if (response.data.payment_status === 'paid') {
                setStatus('success');
                // Optionally fetch booking details
                if (response.data.booking_id) {
                    setBooking({ id: response.data.booking_id });
                }
            } else {
                // Check again in 2 seconds
                setTimeout(checkPaymentStatus, 2000);
            }
        } catch (error) {
            console.error('Error checking status:', error);
            setStatus('error');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            
            <div className="py-16 px-4">
                <div className="max-w-md mx-auto">
                    <Card>
                        <CardContent className="p-8 text-center">
                            {/* Logo */}
                            <div className="flex items-center justify-center gap-2 mb-6">
                                <span className="text-2xl font-bold tracking-tight">
                                    team<span className="text-black">WERK</span>
                                </span>
                                <Building2 className="w-8 h-8" />
                            </div>

                            {status === 'loading' && (
                                <>
                                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
                                    </div>
                                    <h1 className="text-2xl font-bold mb-2">Zahlung wird verarbeitet...</h1>
                                    <p className="text-gray-500">Bitte warten Sie einen Moment.</p>
                                </>
                            )}

                            {status === 'success' && (
                                <>
                                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <CheckCircle2 className="h-10 w-10 text-green-600" />
                                    </div>
                                    <h1 className="text-2xl font-bold mb-2 text-green-700">Buchung bestätigt!</h1>
                                    <p className="text-gray-600 mb-6">
                                        Vielen Dank für Ihre Buchung. Sie erhalten in Kürze eine Bestätigungs-E-Mail mit allen Details.
                                    </p>
                                    
                                    <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                                            <Calendar className="w-4 h-4" />
                                            <span>Buchungsbestätigung wurde per E-Mail versendet</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <Clock className="w-4 h-4" />
                                            <span>Erinnerung 1 Tag vor dem Termin</span>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <Button 
                                            className="w-full bg-black hover:bg-gray-800"
                                            onClick={() => navigate('/buchung')}
                                        >
                                            Weitere Räume buchen
                                        </Button>
                                        <Button 
                                            variant="outline"
                                            className="w-full"
                                            onClick={() => navigate('/')}
                                        >
                                            Zur Startseite
                                        </Button>
                                    </div>
                                </>
                            )}

                            {status === 'error' && (
                                <>
                                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <XCircle className="h-10 w-10 text-red-600" />
                                    </div>
                                    <h1 className="text-2xl font-bold mb-2 text-red-700">Etwas ist schiefgelaufen</h1>
                                    <p className="text-gray-600 mb-6">
                                        Die Zahlung konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut oder kontaktieren Sie uns.
                                    </p>
                                    <Button 
                                        className="w-full bg-black hover:bg-gray-800"
                                        onClick={() => navigate('/buchung')}
                                    >
                                        <ArrowLeft className="mr-2 h-4 w-4" />
                                        Zurück zur Buchung
                                    </Button>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export const BookingCancel = () => {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            
            <div className="py-16 px-4">
                <div className="max-w-md mx-auto">
                    <Card>
                        <CardContent className="p-8 text-center">
                            {/* Logo */}
                            <div className="flex items-center justify-center gap-2 mb-6">
                                <span className="text-2xl font-bold tracking-tight">
                                    team<span className="text-black">WERK</span>
                                </span>
                                <Building2 className="w-8 h-8" />
                            </div>

                            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <XCircle className="h-10 w-10 text-yellow-600" />
                            </div>
                            <h1 className="text-2xl font-bold mb-2">Buchung abgebrochen</h1>
                            <p className="text-gray-600 mb-6">
                                Die Zahlung wurde abgebrochen. Ihre Buchung wurde nicht bestätigt.
                            </p>
                            
                            <div className="space-y-3">
                                <Button 
                                    className="w-full bg-black hover:bg-gray-800"
                                    onClick={() => navigate('/buchung')}
                                >
                                    Erneut versuchen
                                </Button>
                                <Button 
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => navigate('/')}
                                >
                                    Zur Startseite
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default BookingSuccess;
